import { createContext, useContext, useEffect, useState } from 'react'
import { useAuth } from './AuthContext'
import { listCustomers } from '../services/customers'

// Globalt kunde-filter brukt av sider som støtter "vis kun denne kunden".
// Lagres i sessionStorage så det overlever route-bytter, men ikke logout.
// Sider opt-in ved å lese `scopedCustomerId` og sende den som customer_id-
// param i sine API-kall. AuthContext har egen "impersonation" som er sterk-
// ere — kundefilteret påvirker bare data-views, ikke role/permissions.

const CustomerScopeContext = createContext(null)

const STORAGE_KEY = 'customer_scope_id'

export function CustomerScopeProvider({ children }) {
  const { appUser, role, impersonating } = useAuth()
  const [scopedCustomerId, setScopedCustomerIdState] = useState(() => sessionStorage.getItem(STORAGE_KEY) || null)
  const [customers, setCustomers] = useState([])
  const [loaded, setLoaded] = useState(false)

  // Last kundeliste for ikke-kunde-roller (admin/leder/ansatt). Kunder selv
  // har eksakt én kunde og bruker ikke filteret.
  useEffect(() => {
    if (!appUser || role === 'kunde' || impersonating) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoaded(true)
      return
    }
    let alive = true
    listCustomers().then(rows => {
      if (!alive) return
      setCustomers(rows || [])
      setLoaded(true)
    }).catch(() => alive && setLoaded(true))
    return () => { alive = false }
  }, [appUser?.appUserId, role, impersonating])

  function setScopedCustomerId(id) {
    if (id) sessionStorage.setItem(STORAGE_KEY, id)
    else sessionStorage.removeItem(STORAGE_KEY)
    setScopedCustomerIdState(id || null)
  }

  const scopedCustomer = scopedCustomerId
    ? customers.find(c => c.id === scopedCustomerId)
    : null

  return (
    <CustomerScopeContext.Provider value={{
      scopedCustomerId,
      scopedCustomer,
      setScopedCustomerId,
      customers,
      loaded,
    }}>
      {children}
    </CustomerScopeContext.Provider>
  )
}

export const useCustomerScope = () => useContext(CustomerScopeContext) || {
  scopedCustomerId: null,
  scopedCustomer: null,
  setScopedCustomerId: () => {},
  customers: [],
  loaded: true,
}
